import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { task, didCancel } from 'ember-concurrency'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type { IntlService } from 'ember-intl'
import type { BufferedChangeset } from 'ember-changeset/types'
import { trackedFunction } from 'reactiveweb/function'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type Store from '@ember-data/store'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type ErrorHandlerService from 'teacher-ui-client/services/error-handler'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'
import type SessionService from 'teacher-ui-client/services/session'
import type FeaturesService from 'teacher-ui-client/services/features'
import { Changeset } from 'ember-changeset'
import { on } from '@ember/modifier'
import { FormField } from 'teacher-ui-client/components/form-field'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { t } from 'ember-intl'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { pick } from 'teacher-ui-client/helpers/pick'
import { eq, or } from 'ember-truth-helpers'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import submitButtonState from 'teacher-ui-client/helpers/submit-button-state'
import type Accent from 'teacher-ui-client/models/accent'
import SelectBox from 'teacher-ui-client/components/select-box'
import type { SelectOption } from '@blakeelearning/ember-select'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import type Owner from '@ember/owner'
import type StandardsSet from 'teacher-ui-client/models/standards-set'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Args {
  subscriptionType?: SubscriptionType
  isHomeschool?: boolean
  hideStandardsSetSelect?: boolean
}

interface Signature {
  Args: Args
  Element: HTMLFormElement
}

export class FormEditSchoolComponent extends Component<Signature> {
  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service store!: Store

  @service resourceConflict!: ResourceConflictService

  @service('error-handler') errorHandlerService!: ErrorHandlerService

  @service session!: SessionService

  @service features!: FeaturesService

  @tracked errorHandler!: ErrorHandler

  @tracked schoolChangeset!: BufferedChangeset

  constructor(owner: Owner, args: Args) {
    super(owner, args)

    this.schoolChangeset = Changeset(this.session.currentSchool)
    void this.schoolChangeset.validate()

    this.errorHandler = this.errorHandlerService.create()
  }

  accents = trackedFunction(this, async () => {
    await Promise.resolve() // disconnect tracking before store calls
    return await this.store.query<Accent>('accent', {})
  })

  get accentOptions() {
    const accents = (this.accents.value ?? []).filter(
      // a school already on nz keeps it as an option
      (accent) =>
        accent.extension !== 'nz' ||
        this.features.isEnabled('tui_nz_accent') ||
        accent.id === this.schoolChangeset.get('data.accent.id'),
    )
    return accents.map((accent) => ({
      value: accent.id,
      label: this.intl.t(`components.form.editSchool.accentOptions.${accent.extension}`),
    }))
  }

  get accentId() {
    // school based accent should always be set
    return this.schoolChangeset.get('accent.id')
  }

  set accentId(value: string) {
    const { schoolChangeset } = this
    const accentId = value
    // is the newly selected accent different to the original accent?
    if (schoolChangeset.get('data.accent.id') === accentId) {
      schoolChangeset.rollbackProperty('accent')
    } else {
      const accents = this.accents.value ?? []
      const accent = accents.find((acc) => acc.id === accentId)
      if (accent) schoolChangeset.set('accent', accent)
    }
    // validate after setting a relationship
    void schoolChangeset.validate()
  }

  saveSchool = task({ drop: true }, async ({ schoolChangeset }) => {
    await schoolChangeset.save()
  })

  private rollback = () => {
    if (this.schoolChangeset.isDirty && this.saveSchool.isIdle) {
      this.schoolChangeset.rollback()
    }
  }

  onReset = (event: Event) => {
    event.preventDefault()
    this.rollback()
  }

  onSubmit = async (event: Event) => {
    event.preventDefault()

    const { errorHandler, schoolChangeset } = this
    try {
      await this.saveSchool.perform({ schoolChangeset })

      const name = schoolChangeset.get('name')
      const updateMessage = this.intl.t('components.form.editSchool.success', { name })
      this.flashQueue.addSuccess({ subtitle: updateMessage })
    } catch (e: any) {
      if (!didCancel(e)) {
        const errors = e?.errors || []
        errorHandler.handle(errors)
        errorHandler.sendOtherErrorsToLog('Error while updating school from the "edit school" component')

        const school = schoolChangeset.get('data')
        school.rollbackAttributes()

        if (isConflictError(e)) {
          return this.resourceConflict.handleError('update school', e)
        }
        const name = schoolChangeset.get('name')
        const message = this.intl.t('components.form.editSchool.error', { name })

        if (errorHandler.hasUnhandledErrors()) this.flashQueue.addFail({ subtitle: message })
      }
    }
  }

  setAccentId = (value: string) => {
    this.accentId = value
  }

  loadStandardsOptions = trackedFunction(this, async () => {
    const { countryCode } = this.session.currentSchool

    await Promise.resolve() // disconnect tracking before store calls

    const standardsSets = (await this.store.query<StandardsSet>('standards-set', {})).filter(hasId)
    const regionNames = new Intl.DisplayNames([], { type: 'region' }) // uses locale set in the browser
    // sort by matching country code group first, then alphabetical groups + alphabetical options in each group (but CCSS first)
    return standardsSets
      .sort((a, b) => {
        // if same countrycode group
        if (a.countryCode === b.countryCode) {
          if (a.code === 'CCSS') return -1 // CCSS option always first if present within group
          if (b.code === 'CCSS') return 1
          if (a.name === 'Australia V9') return -1 // Australia V9 option always first if present within group
          if (b.name === 'Australia V9') return 1
          return a.name.localeCompare(b.name) // otherwise sort by option label
        }
        // else differing countrycode groups - if either group matches the current country code, it goes first
        if (a.countryCode === countryCode) return -1
        if (b.countryCode === countryCode) return 1
        return a.countryCode.localeCompare(b.countryCode) // otherwise alphabetical groups
      })
      .map((standard) => ({
        value: standard.id,
        label: `${standard.name} (${standard.code})`,
        group: regionNames.of(standard.countryCode),
      }))
  })

  get standardsOptions(): SelectOption[] {
    return this.loadStandardsOptions.value ?? []
  }

  onSelectStandardsSet = ({ value }: SelectOption) => {
    const standardsSet = this.store.peekRecord<StandardsSet>('standards-set', value)
    this.schoolChangeset.set('standardsSet', standardsSet)
  }

  get hasDisco() {
    return Boolean(this.session.currentSchool.district?.districtCoordinators?.length)
  }

  get currentStandardsSet(): string | undefined {
    return this.schoolChangeset.get('standardsSet')?.id as string | undefined
  }

  get formTitle() {
    return this.args.isHomeschool
      ? this.intl.t('components.form.reportSettings.editDetails')
      : this.intl.t('components.form.editSchool.editDetails')
  }

  get changesWarning() {
    return this.args.isHomeschool
      ? this.intl.t('components.form.reportSettings.changesWarning')
      : this.intl.t('components.form.editSchool.changesWarning')
  }

  <template>
    <form
      data-test-edit-school
      class="space-y-3"
      autocomplete="off"
      {{on "submit" this.onSubmit}}
      {{on "reset" this.onReset}}
      ...attributes
    >
      <div class="font-semibold">{{this.formTitle}}</div>
      <div>{{this.changesWarning}}</div>
      <div class="grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-cols-3">
        <FormField
          @changeset={{this.schoolChangeset}}
          @propertyName="accent"
          @labelText={{t "components.form.editSchool.accent"}}
          as |field|
        >
          <field.label class="font-normal" @helpText={{t "components.form.editSchool.accentTooltip"}} />
          {{#if this.accents.isPending}}
            <div class="mt-3 mb-2.5"><FidgetSpinnerWaveComponent @centered={{true}} @small={{true}} /></div>
          {{else}}
            <BaseSelect
              id={{field.fieldId}}
              name="accent"
              class="w-full"
              @themeKey={{themeKey @subscriptionType}}
              value={{this.accentId}}
              {{on "input" (pick "target.value" this.setAccentId)}}
            >
              {{#each this.accentOptions as |opt|}}
                <option value={{opt.value}} selected={{eq opt.value this.accentId}}>{{opt.label}}</option>
              {{/each}}
            </BaseSelect>
          {{/if}}
        </FormField>
        {{#unless @hideStandardsSetSelect}}
          <FormField
            class="col-start-1"
            @changeset={{this.schoolChangeset}}
            @propertyName="standardsSet"
            @labelText={{t "components.form.editSchool.academicStandards"}}
            as |field|
          >
            <field.label
              data-test-edit-school-select-box-label
              class="font-normal"
              @helpText={{unless this.hasDisco (t "components.form.editSchool.standaloneTooltip")}}
            />

            {{#if this.loadStandardsOptions.isPending}}
              <div class="mt-3 mb-2.5"><FidgetSpinnerWaveComponent @centered={{true}} @small={{true}} /></div>
            {{else}}
              <span>
                {{#if this.hasDisco}}
                  <Tooltip data-test-edit-school-tooltip @text={{t "components.form.editSchool.districtTooltip"}} />
                {{/if}}
                <SelectBox
                  data-test-edit-school-standardsSet
                  id={{field.fieldId}}
                  name="standardsSet"
                  class="w-full"
                  @placeholder={{t "components.form.editSchool.standardsSelectBox.placeholder"}}
                  @searchPlaceholder={{t "components.form.editSchool.standardsSelectBox.searchPlaceholder"}}
                  @value={{this.currentStandardsSet}}
                  @options={{this.standardsOptions}}
                  @onSelect={{this.onSelectStandardsSet}}
                  @disabled={{this.hasDisco}}
                />
              </span>
            {{/if}}
          </FormField>
        {{/unless}}

      </div>

      <div class="text-center">
        <UiButton
          data-test-edit-school-reset
          disabled={{this.schoolChangeset.isPristine}}
          @appearance={{ButtonAppearance.muted}}
          type="reset"
        >
          {{t "cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-edit-school-submit
          @disabled={{or this.schoolChangeset.isPristine this.schoolChangeset.isInvalid}}
          @appearance={{ButtonAppearance.regular}}
          @defaultText={{t "saveChanges"}}
          @state={{submitButtonState this.saveSchool.last this.errorHandler}}
        />
      </div>
    </form>
  </template>
}

export default FormEditSchoolComponent
